import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Users as UsersIcon, UserPlus, Loader2, Trash2, Save, Copy, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["member", "technology", "pr_chairman", "comms_team", "comms_admin", "president", "vice_president"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", role: "member", committee: "" });
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState(null); // {email, temp_password}
  const [savingId, setSavingId] = useState(null);
  const [editUser, setEditUser] = useState(null); // {user_id, name, email}
  const [savingEdit, setSavingEdit] = useState(false);

  const saveEdit = async () => {
    if (!editUser.name.trim() || !editUser.email.trim()) { toast.error("Name and email are required"); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/users/${editUser.user_id}`, { name: editUser.name, email: editUser.email });
      setUsers((prev) => prev.map((x) => (x.user_id === data.user_id ? data : x)));
      toast.success("Member updated");
      setEditUser(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingEdit(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error("Could not load users"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/committees").then(({ data }) => setCommittees(data.committees || [])).catch(() => {}); }, []);

  const submitInvite = async () => {
    if (!invite.name.trim() || !invite.email.trim()) { toast.error("Name and email are required"); return; }
    setInviting(true); setInvited(null);
    try {
      const { data } = await api.post("/users", {
        name: invite.name, email: invite.email, role: invite.role,
        committee: invite.committee || null,
      });
      setInvited({ email: data.user.email, temp_password: data.temp_password });
      setInvite({ name: "", email: "", role: "member", committee: "" });
      toast.success("Member invited — a welcome email was sent");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setInviting(false); }
  };

  const patchUser = async (u, changes) => {
    setSavingId(u.user_id);
    try {
      const { data } = await api.put(`/users/${u.user_id}`, changes);
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? data : x)));
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingId(null); }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      toast.success("Account removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const initials = (n) => (n || "U").replace(/^soror\s+/i, "").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="mx-auto max-w-5xl space-y-8" data-testid="users-page">
      <datalist id="committee-suggestions">
        {committees.map((c) => <option key={c} value={c} />)}
      </datalist>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><UsersIcon className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Members & Roles</h1>
            <p className="text-sm text-muted-foreground">Invite members and manage names, roles, and committees.</p>
          </div>
        </div>
        <Dialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) setInvited(null); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="invite-member-btn"><UserPlus className="mr-1.5 h-4 w-4" /> Invite member</Button>
          </DialogTrigger>
          <DialogContent data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle>Invite a member</DialogTitle>
              <DialogDescription>They'll get a welcome email with a temporary password and a sign-in link.</DialogDescription>
            </DialogHeader>
            {invited ? (
              <div className="space-y-3" data-testid="invite-success">
                <div className="flex items-center gap-2 text-primary"><CheckCircle2 className="h-5 w-5" /> <span className="font-semibold">Invite sent to {invited.email}</span></div>
                <div className="rounded-lg border border-dashed border-border bg-secondary/40 p-3 text-sm">
                  <p className="text-muted-foreground">Temporary password (also emailed):</p>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="rounded bg-background px-2 py-1 font-mono" data-testid="temp-password">{invited.temp_password}</code>
                    <Button size="sm" variant="outline" className="h-7 rounded-full"
                      onClick={() => { navigator.clipboard.writeText(invited.temp_password); toast.success("Copied"); }}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <Button className="w-full rounded-full" onClick={() => setInvited(null)} data-testid="invite-another-btn">Invite another</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div><Label htmlFor="inv-name">Full name</Label><Input id="inv-name" data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="mt-1.5" /></div>
                <div><Label htmlFor="inv-email">Email</Label><Input id="inv-email" type="email" data-testid="invite-email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="mt-1.5" placeholder="member@alphaepsilongammaomega.org" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Role</Label>
                    <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`invite-role-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="inv-committee">Committee</Label>
                    <Input id="inv-committee" data-testid="invite-committee" list="committee-suggestions"
                      value={invite.committee} onChange={(e) => setInvite({ ...invite, committee: e.target.value })}
                      className="mt-1.5" placeholder="Type a committee (optional)" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submitInvite} disabled={inviting} className="w-full rounded-full" data-testid="invite-submit">
                    {inviting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1.5 h-4 w-4" />} Send invite
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <Card className="divide-y divide-border p-0" data-testid="users-list">
          {users.map((u) => (
            <div key={u.user_id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-testid={`user-row-${u.email}`}>
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(u.name)}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <p className="truncate font-semibold">{u.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={u.role} onValueChange={(v) => patchUser(u, { role: v })}>
                  <SelectTrigger className="h-9 w-[180px]" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
                <Input defaultValue={u.committee || ""} list="committee-suggestions" placeholder="Committee"
                  data-testid={`committee-input-${u.email}`} className="h-9 w-[190px]"
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== (u.committee || "")) patchUser(u, { committee: v || null }); }} />
                {savingId === u.user_id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <Button variant="outline" size="icon" className="h-9 w-9" data-testid={`edit-user-${u.email}`}
                  onClick={() => setEditUser({ user_id: u.user_id, name: u.name || "", email: u.email || "" })}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="icon" className="h-9 w-9" data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                      <AlertDialogDescription>This deletes their account and they will no longer be able to sign in. Their past submissions are kept.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => removeUser(u)} data-testid="delete-confirm">Remove account</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </Card>
      )}

      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit member</DialogTitle>
            <DialogDescription>Update this member's name and email address.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Full name</Label>
                <Input id="edit-name" data-testid="edit-name-input" value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" type="email" data-testid="edit-email-input" value={editUser.email}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} className="mt-1.5" />
              </div>
              <DialogFooter>
                <Button onClick={saveEdit} disabled={savingEdit} className="w-full rounded-full" data-testid="edit-save-btn">
                  {savingEdit ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
